import { useState, useEffect, useCallback } from 'react'

// Module-level cache — survives re-renders and route changes
const streamCache = new Map()

const PLATFORMS = [
    { key: 'spotify', name: 'Spotify', icon: '🟢', searchUrl: (a, t) => `https://open.spotify.com/search/${encodeURIComponent(a + ' ' + t)}` },
    { key: 'appleMusic', name: 'Apple Music', icon: '🍎', searchUrl: (a, t) => `https://music.apple.com/search?term=${encodeURIComponent(a + ' ' + t)}` },
    { key: 'deezer', name: 'Deezer', icon: '🎵', searchUrl: (a, t) => `https://www.deezer.com/search/${encodeURIComponent(a + ' ' + t)}` },
    { key: 'youtubeMusic', name: 'YouTube Music', icon: '▶️', searchUrl: (a, t) => `https://music.youtube.com/search?q=${encodeURIComponent(a + ' ' + t)}` },
    { key: 'tidal', name: 'Tidal', icon: '🌊', searchUrl: (a, t) => `https://listen.tidal.com/search?q=${encodeURIComponent(a + ' ' + t)}` },
]

export function useStreamingLinks() {
    const [openTrack, setOpenTrack] = useState(null)
    const [links, setLinks] = useState(null)
    const [loading, setLoading] = useState(false)

    const open = useCallback(async (track) => {
        if (!track?.artist || !track?.title) return
        setOpenTrack(track)
        setLinks(null)
        setLoading(true)

        const cacheKey = `${track.artist}::${track.title}`

        // Check cache first
        if (streamCache.has(cacheKey)) {
            setLinks(streamCache.get(cacheKey))
            setLoading(false)
            return
        }

        try {
            const q = encodeURIComponent(`${track.artist} ${track.title}`)
            const dzRes = await fetch(`https://corsproxy.io/?url=${encodeURIComponent(`https://api.deezer.com/search?q=${q}&limit=1`)}`)
            const dzData = await dzRes.json()
            if (dzData.data?.[0]?.link) {
                const songUrl = dzData.data[0].link
                const olRes = await fetch(`https://api.song.link/v1-alpha.1/links?url=${encodeURIComponent(songUrl)}`)
                if (olRes.ok) {
                    const olData = await olRes.json()
                    const result = {}
                    if (olData.linksByPlatform?.spotify) result.spotify = olData.linksByPlatform.spotify.url
                    if (olData.linksByPlatform?.appleMusic) result.appleMusic = olData.linksByPlatform.appleMusic.url
                    if (olData.linksByPlatform?.deezer) result.deezer = olData.linksByPlatform.deezer.url
                    if (olData.linksByPlatform?.youtubeMusic) result.youtubeMusic = olData.linksByPlatform.youtubeMusic.url
                    if (olData.linksByPlatform?.tidal) result.tidal = olData.linksByPlatform.tidal.url
                    streamCache.set(cacheKey, result)
                    setLinks(result)
                }
            }
        } catch { /* fallback to search URLs */ }
        setLoading(false)
    }, [])

    const close = useCallback(() => {
        setOpenTrack(null)
        setLinks(null)
        setLoading(false)
    }, [])

    // Escape key
    useEffect(() => {
        if (!openTrack) return
        const onKey = (e) => { if (e.key === 'Escape') close() }
        document.addEventListener('keydown', onKey)
        document.body.style.overflow = 'hidden'
        return () => {
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = ''
        }
    }, [openTrack, close])

    return { openTrack, links, loading, open, close, PLATFORMS }
}
